// Sprint monitor-tool-live-timer — format an elapsed-ms value as the
// "Waiting Xs / Xm Ys / Xh Ym" string Cursor shows for long-running
// background tasks.
//
// The output goes through i18n so the Spanish locale reads "Esperando".
// Always rounds down to whole seconds; negative inputs clamp to 0 (a
// startedAtMs slightly in the future during clock drift should never
// render a negative timer).
//
// Sprint turn-collapse-final-answer (D5) — the seconds/minutes/hours
// split is shared with the collapsed-turn header ("Worked for 4m 44s").
// One rounding rule, two phrasings: a turn that reads 4m 44s here and
// 4m 45s there would be two truths about the same number.

import type { TKey } from '../../i18n';

type Translator = (key: TKey, params?: Record<string, string | number>) => string;

export type ElapsedSplit =
  | { unit: 'seconds'; s: number }
  | { unit: 'minutes'; m: number; s: number }
  | { unit: 'hours'; h: number; m: number };

export function splitElapsed(elapsedMs: number): ElapsedSplit {
  const totalSec = Math.max(0, Math.floor(elapsedMs / 1000));
  if (totalSec < 60) return { unit: 'seconds', s: totalSec };
  if (totalSec < 3600) {
    return { unit: 'minutes', m: Math.floor(totalSec / 60), s: totalSec % 60 };
  }
  return {
    unit: 'hours',
    h: Math.floor(totalSec / 3600),
    m: Math.floor((totalSec % 3600) / 60),
  };
}

export function formatElapsed(elapsedMs: number, t: Translator): string {
  const split = splitElapsed(elapsedMs);
  if (split.unit === 'seconds') return t('toolCall.waitingSeconds', { s: split.s });
  if (split.unit === 'minutes') {
    return t('toolCall.waitingMinutes', { m: split.m, s: split.s });
  }
  return t('toolCall.waitingHours', { h: split.h, m: split.m });
}

/**
 * "Worked for 44s" / "Worked for 4m 44s" / "Worked for 1h 12m" — the
 * duration line on a collapsed turn header. `null` (no duration could be
 * derived) falls back to the bare verb.
 */
export function formatWorkedFor(durationMs: number | null, t: Translator): string {
  if (durationMs === null) return t('turnCollapse.worked');
  const split = splitElapsed(durationMs);
  if (split.unit === 'seconds') return t('turnCollapse.workedForSeconds', { s: split.s });
  if (split.unit === 'minutes') {
    return t('turnCollapse.workedForMinutes', { m: split.m, s: split.s });
  }
  return t('turnCollapse.workedForHours', { h: split.h, m: split.m });
}

/**
 * Sprint compact-action-rows (D6) — how long a finished action or a run of
 * them took, in the compact form the row appends after the headline:
 * `44s` / `4m 44s` / `1h 12m`. Same `splitElapsed` rounding as the live
 * ticker and the turn line, so one action cannot read two ways depending
 * on which surface shows it. `null` in ⇒ nothing rendered, never `NaN`
 * and never a misleading `0s`.
 */
export function formatActionDuration(durationMs: number | null, t: Translator): string | null {
  if (durationMs === null || !Number.isFinite(durationMs) || durationMs < 0) return null;
  const split = splitElapsed(durationMs);
  if (split.unit === 'seconds') return t('actionRow.durationSeconds', { s: split.s });
  if (split.unit === 'minutes') {
    return t('actionRow.durationMinutes', { m: split.m, s: split.s });
  }
  return t('actionRow.durationHours', { h: split.h, m: split.m });
}

/**
 * Sprint compact-action-rows (D5) — wall-clock `HH:MM` for a row, in the
 * viewer's locale and timezone. Santi asked for the hour explicitly; the
 * data has always been on every message and was rendered nowhere.
 * Unparseable input renders nothing rather than `Invalid Date`.
 */
export function formatClockTime(atMs: number | null): string | null {
  if (atMs === null || !Number.isFinite(atMs)) return null;
  return new Date(atMs).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}
